import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, ArrowRight, Sparkles, Trophy, ShieldCheck, Crown, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_BADGE = {
  WAITLIST: 'bg-slate-200 text-slate-800 border-slate-300',
  ROOKIE: 'bg-blue-100 text-blue-800 border-blue-300',
  PRO: 'bg-purple-100 text-purple-800 border-purple-300',
  ENTERPRISE: 'bg-amber-100 text-amber-800 border-amber-300',
  ADMIN: 'bg-red-100 text-red-800 border-red-300',
};

const STATE_ACCENT = {
  waitlist: 'from-slate-100 to-slate-50 border-slate-200',
  rookie: 'from-blue-50 to-white border-blue-200',
  phase2_unlocked: 'from-emerald-50 to-white border-emerald-200',
  pro: 'from-purple-50 to-white border-purple-200',
  enterprise: 'from-amber-50 to-white border-amber-200',
  admin: 'from-rose-50 to-white border-rose-200',
};

const STATE_ICON = {
  waitlist: Lock,
  rookie: Trophy,
  phase2_unlocked: ShieldCheck,
  pro: Sparkles,
  enterprise: Crown,
  admin: ShieldCheck,
};

const CREDIT_LABEL = {
  AD_CREDIT: 'Ad',
  LEAD_CREDIT: 'Lead',
  BOOST_CREDIT: 'Boost',
  TRAINING_CREDIT: 'Training',
  GAME_CREDIT: 'Game',
};

const TierProgressionWidget = ({ compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/member/tier/progression`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Unable to load progression');
          setLoading(false);
        }
      }
    };
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  if (loading || !data) {
    return (
      <Card data-testid="tier-progression-loading" className="border-dashed">
        <CardContent className="py-6 text-sm text-muted-foreground text-center">
          {error || 'Loading your progression…'}
        </CardContent>
      </Card>
    );
  }

  const { state, current_tier, next_tier, next_tier_bundle, progress, copy, cta } = data;
  const accent = STATE_ACCENT[state] || STATE_ACCENT.waitlist;
  const Icon = STATE_ICON[state] || Trophy;
  const bundleEntries = Object.entries(next_tier_bundle || {}).filter(([, v]) => Number(v) > 0);

  return (
    <Card
      data-testid={`tier-progression-widget-${state}`}
      className={`bg-gradient-to-br ${accent} border`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
              <Icon className="h-5 w-5 text-slate-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg truncate" data-testid="tier-progression-headline">
                {copy.headline}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={TIER_BADGE[current_tier] || ''} data-testid="tier-progression-current">
                  {current_tier}
                </Badge>
                {next_tier && (
                  <>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Badge variant="outline" data-testid="tier-progression-next">
                      {next_tier}
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-slate-700" data-testid="tier-progression-message">
          {copy.message}
        </p>

        {/* Progress bar */}
        <div data-testid="tier-progression-progress">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
            <span>{progress.label}</span>
            <span className="font-medium">{progress.percent}%</span>
          </div>
          <div className="w-full h-2 bg-white/70 rounded-full overflow-hidden border border-slate-200">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }}
              data-testid="tier-progression-progress-bar"
            />
          </div>
        </div>

        {/* Next tier bundle preview */}
        {!compact && bundleEntries.length > 0 && (
          <div className="rounded-lg bg-white/80 border border-slate-200 p-3" data-testid="tier-progression-bundle">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-700 mb-2">
              <Gift className="h-3 w-3" />
              You unlock when you reach <b className="ml-1">{next_tier}</b>:
            </div>
            <div className="flex flex-wrap gap-2">
              {bundleEntries.map(([ct, amt]) => (
                <Badge key={ct} variant="secondary" className="text-xs" data-testid={`tier-progression-bundle-${ct}`}>
                  +{amt} {CREDIT_LABEL[ct] || ct}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <Button
          onClick={() => navigate(cta.path)}
          className="w-full sm:w-auto"
          data-testid="tier-progression-cta-btn"
        >
          {cta.label}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default TierProgressionWidget;
